import React, { useCallback, useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import { useToast } from "components/toast";

import {
  LocationClientProfile,
  getProfileById,
  updateProfile,
} from "api/LocationClientProfiles";

import styles from "./styles.module.scss";

interface Props {
  profileId: string;
  onClose: () => void;
  /** Called after a successful save so the parent can refetch. */
  onSaved?: () => void;
}

/**
 * Edit the notes on a LocationClientProfile — the (Location, Client)
 * relationship record.
 *
 * Session 26 Batch #9: this modal used to also carry primary + additional
 * contact pickers and on-site contact fields. Those all moved down to
 * the mulch_map version in Batch #6, are exposed in the MapEditor
 * sidebar in Batch #8, and inherit onto records at attach time in
 * Batch #7. What's left on the LCP is per-client-at-this-site notes
 * (depth preferences, timing constraints, etc.) — a small enough
 * concern that the modal is now a single field.
 *
 * The old fields on LCP (primaryClientId, contacts M2M, siteContact*)
 * are being kept in the DB until Batch #10 drops them; the UI just
 * doesn't surface them anymore. `updateProfile` only sends fields we
 * include in the payload, so those columns aren't touched by saves
 * from this modal.
 */
const ProfileEditModal: React.FC<Props> = ({ profileId, onClose, onSaved }) => {
  const toast = useToast();

  const [profile, setProfile] = useState<LocationClientProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [notes, setNotes] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      const p = await getProfileById(profileId);
      if (cancelled) return;
      if (!p) {
        setError("Profile not found.");
        setLoading(false);
        return;
      }

      setProfile(p);
      setNotes(p.notes ?? "");
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);

    // Only include the notes field in the payload. Other columns
    // (primaryClientId, contactIds, siteContact*, name, poConvention)
    // are absent from the DTO here so the backend leaves them alone —
    // they're read-only history at this point, dropped in Batch #10.
    const res = await updateProfile(profileId, {
      notes: notes.trim() || null,
    });

    setSaving(false);

    if ("error" in res) {
      setError(res.error);
      return;
    }

    toast.add("Notes saved.", "var(--theme-ui-colors-success)");
    onSaved?.();
    onClose();
  }, [profileId, notes, toast, onSaved, onClose]);

  const orgName = profile?.client?.name ?? "this client";

  return (
    <Modal isOpen={true} toggle={onClose} size="md" centered>
      <ModalHeader toggle={onClose}>
        {loading ? "Client notes at this site" : `Notes for ${orgName} at this site`}
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <p className={styles.status}>Loading&hellip;</p>
        ) : (
          <>
            <p className={styles.blurb}>
              Client-specific preferences at this site (e.g. depth
              preferences, timing constraints). Physical site notes like
              gate codes belong on the Location page. Contacts for this
              client live on individual map versions &mdash; open a map in
              the editor to set them.
            </p>

            <div className={styles.field}>
              <TextInput
                name="notes"
                label="Client notes at this site"
                value={notes}
                onChange={(v: any) => setNotes(v)}
                $customStyles={{ width: "100%", marginBottom: 0 }}
              />
            </div>

            {error && <p className={styles.errorLine}>{error}</p>}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <BasicButton
          type="button"
          scheme="secondary"
          outline
          onClick={onClose}
          styles={{ margin: "0 10px 0 0" }}
        >
          Cancel
        </BasicButton>
        <BasicButton
          type="button"
          onClick={handleSave}
          disabled={loading || saving}
          $submitting={saving}
          styles={{ margin: "0" }}
          keepText
        >
          Save notes
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
};

export default ProfileEditModal;
